"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { useRouter } from "next/navigation";
import styles from "./page.module.css";

interface EmailPasswordAuthProps {
    isRegisterMode: boolean;
}

export function EmailPasswordAuth({ isRegisterMode }: EmailPasswordAuthProps) {
    const t = useTranslations("home.auth");
    const router = useRouter();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const validateForm = (): boolean => {
        if (!email.trim() || !email.includes("@")) {
            setError(t("errors.invalidEmail"));
            return false;
        }

        if (isRegisterMode && password.length < 8) {
            setError(t("errors.passwordTooShort"));
            return false;
        }

        if (!password) {
            setError(t("errors.passwordTooShort"));
            return false;
        }

        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const endpoint = isRegisterMode
                ? "/api/account/password/register"
                : "/api/account/password/login";

            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });

            if (response.ok) {
                router.push("/dashboard");
                router.refresh();
            } else {
                const data = await response.json().catch(() => ({}));
                setError(
                    data.error ||
                        (isRegisterMode
                            ? t("errors.registrationFailed")
                            : t("errors.loginFailed")),
                );
            }
        } catch (err) {
            console.error("Auth error:", err);
            setError(t("errors.unknownError"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <form className={styles.form} onSubmit={handleSubmit}>
            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                    {t("email")}
                </label>
                <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("emailPlaceholder")}
                    className={styles.input}
                    disabled={loading}
                    required
                />
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.label}>
                    {t("password")}
                </label>
                <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("passwordPlaceholder")}
                    className={styles.input}
                    disabled={loading}
                    required
                    minLength={8}
                />
            </div>

            <button type="submit" className={styles.button} disabled={loading}>
                {loading && <span className={styles.spinner} />}
                {loading
                    ? isRegisterMode
                        ? t("registering")
                        : t("loggingIn")
                    : isRegisterMode
                      ? t("register")
                      : t("login")}
            </button>
        </form>
    );
}
